import * as React from 'react';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { AdminSiteDetail, useAdminSiteInfoDetails, useUpsertSiteDetail } from '../../hooks/useSiteDetails';
import SiteInfoClass from '../site/SiteInfo';
import { LANGUAGE } from '../../constants/storageKeys';
import * as i18next from 'i18next';
import AdminSiteCharacteristics from './AdminSiteCharacteristics';
import { flags } from '../../constants/flags';
import DefaultPromptModal from '../Controls/DefaultPromptModal';
import Spinner from '../Controls/Spinner';
import TextArea from 'react-textarea-autosize';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';

function SiteDetailForm({ match }: RouteComponentProps<{ siteId: string; cultureId: string }>) {
    const uniqueSiteId = parseInt(match.params.siteId);
    const activeCultureId = match.params.cultureId;
    const defaultCultureId = localStorage[LANGUAGE];

    const [siteDetail, setSiteDetail] = React.useState<AdminSiteDetail[]>([]);
    const [goBack, setGoBack] = React.useState(false);
    const [upsertedDetails, setUpsertedDetails] = React.useState(false);

    const { data: adminSiteInfoDetail, isLoading } = useAdminSiteInfoDetails(uniqueSiteId, activeCultureId, defaultCultureId);
    const upsertMutation = useUpsertSiteDetail();

    React.useEffect(() => {
        if (adminSiteInfoDetail && adminSiteInfoDetail.siteDetail.length > 0) {
            setSiteDetail(adminSiteInfoDetail.siteDetail);
        }
    }, [adminSiteInfoDetail]);

    React.useEffect(() => {
        if (upsertMutation.isSuccess) setUpsertedDetails(true);
    }, [upsertMutation.isSuccess]);

    if (goBack) return <Redirect to={'/administrationsites'} />;
    if (isLoading) return <Spinner />;

    function updateState(e: React.ChangeEvent<HTMLTextAreaElement>) {
        const id = e.target.id;
        const value = e.target.value;
        setSiteDetail(prev => prev.map(sd => sd.column === id ? { ...sd, value } : sd));
    }

    function createFormData(siteDetails: AdminSiteDetail[]) {
        const formData = new FormData();
        formData.append('cultureId', activeCultureId);
        siteDetails.forEach(sd => formData.append(sd.column.substring(5), sd.value));
        return formData;
    }

    function upsertSiteDetail(e: React.MouseEvent) {
        e.preventDefault();
        upsertMutation.mutate({ uniqueSiteId, data: createFormData(siteDetail) });
    }

    function renderFlag() {
        const flag = flags.find(x => x.cultureCode === activeCultureId);
        return flag
            ? <span title={i18next.t(flag.languageTag)} className={flag.flag + 'loginFlags'} style={{ width: 30, height: 25, cursor: 'default', display: 'inline-block' }}></span>
            : null;
    }

    function renderSiteDetail(sd: AdminSiteDetail) {
        if (sd.isPicture) return null;
        return (
            <Grid key={sd.column} container alignItems="flex-start" spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                    {sd.defaultValue && <Box sx={{ height: 20 }} />}
                    <Typography component="label" htmlFor={sd.column} sx={{ display: 'block' }}>
                        {i18next.t(sd.column)}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 8 }}>
                    {sd.defaultValue && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, wordBreak: 'break-word' }}>
                            [{sd.defaultValue}]
                        </Typography>
                    )}
                    <TextArea
                        minRows={2}
                        className="form-control"
                        style={{ resize: 'none', width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit', fontSize: '1rem' }}
                        id={sd.column}
                        value={sd.value}
                        onChange={updateState}
                    />
                </Grid>
            </Grid>
        );
    }

    const siteInfo = adminSiteInfoDetail?.siteInfo;

    return (
        <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h5" fontWeight={600}>
                        {i18next.t('adminsites.changeproperties')}
                    </Typography>
                    {renderFlag()}
                </Box>
                <Divider sx={{ mb: 3 }} />

                {siteInfo && (
                    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2, mb: 3 }}>
                        <SiteInfoClass key={siteInfo.uniqueSiteId + siteInfo.siteName} site={siteInfo} fromAdmin={true} includeState={true} />
                    </Paper>
                )}

                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {siteDetail.map(sd => renderSiteDetail(sd))}

                    <Divider />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button variant="contained" color="primary" onClick={upsertSiteDetail}>
                            {i18next.t('adminsites.updatebutton')}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={() => setGoBack(true)}>
                            {i18next.t('adminsites.cancelbutton')}
                        </Button>
                    </Box>
                </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <AdminSiteCharacteristics uniqueSiteId={uniqueSiteId} />
            </Grid>

            <DefaultPromptModal handleClose={() => setUpsertedDetails(false)} body={i18next.t('adminsites.updatedbody')}
                header={i18next.t('adminsites.updatedheader')} show={upsertedDetails} closeText={i18next.t('common.close')} />
        </Grid>
    );
}

export default SiteDetailForm;
